import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const COARSE = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;
const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

// Full-screen product photo viewer — swipe/arrow between images, pinch-zoom (touch), click-zoom (desktop)
export const PhotoViewer = ({ images, index, onIndexChange, onClose, alt }) => {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [origin, setOrigin] = useState("50% 50%");
  const startX = useRef(0);
  const pinch = useRef({ active: false, startDist: 0, startScale: 1 });
  const pan = useRef({ active: false, x: 0, y: 0, tx: 0, ty: 0 });
  const lastTap = useRef(0);
  const n = images.length;

  const go = useCallback((dir) => {
    setScale(1); setTx(0); setTy(0); setOrigin("50% 50%");
    onIndexChange((index + dir + n) % n);
  }, [index, n, onIndexChange]);

  useEffect(() => { setScale(1); setTx(0); setTy(0); setOrigin("50% 50%"); }, [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && n > 1) go(1);
      else if (e.key === "ArrowLeft" && n > 1) go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [go, onClose, n]);

  const toggleZoom = () => {
    if (scale > 1) { setScale(1); setTx(0); setTy(0); }
    else setScale(2.5);
  };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinch.current = { active: true, startDist: dist(e.touches), startScale: scale };
      return;
    }
    startX.current = e.touches[0].clientX;
    if (scale > 1) pan.current = { active: true, x: e.touches[0].clientX, y: e.touches[0].clientY, tx, ty };
    const now = Date.now();
    if (now - lastTap.current < 280) toggleZoom();
    lastTap.current = now;
  };

  const onTouchMove = (e) => {
    if (pinch.current.active && e.touches.length === 2) {
      const ratio = dist(e.touches) / (pinch.current.startDist || 1);
      setScale(Math.max(1, Math.min(4, pinch.current.startScale * ratio)));
    } else if (pan.current.active && scale > 1 && e.touches.length === 1) {
      setTx(pan.current.tx + (e.touches[0].clientX - pan.current.x));
      setTy(pan.current.ty + (e.touches[0].clientY - pan.current.y));
    }
  };

  const onTouchEnd = (e) => {
    if (pinch.current.active) {
      pinch.current.active = false;
      if (scale <= 1.05) { setScale(1); setTx(0); setTy(0); }
      return;
    }
    if (pan.current.active) { pan.current.active = false; return; }
    if (scale === 1 && n > 1 && e.changedTouches.length) {
      const dx = e.changedTouches[0].clientX - startX.current;
      if (dx > 45) go(-1);
      else if (dx < -45) go(1);
    }
  };

  const onClickImg = (e) => {
    if (COARSE) return;
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
    setScale((s) => (s > 1 ? 1 : 2.5));
  };

  const onMoveImg = (e) => {
    if (COARSE || scale === 1) return;
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
  };

  const gesturing = pinch.current.active || pan.current.active;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col select-none"
      style={{ backgroundColor: "rgba(11,10,8,0.97)" }}
      onClick={onClose}
      data-testid="product-photo-viewer"
    >
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 text-[#e9d9ac]" onClick={(e) => e.stopPropagation()}>
        <span className="text-xs tracking-[0.2em] uppercase text-[#c9a24b]" data-testid="photo-viewer-counter">
          {index + 1} / {n}
        </span>
        <button onClick={onClose} aria-label="Close photo viewer" data-testid="photo-viewer-close"
          className="h-10 w-10 flex items-center justify-center rounded-full border border-[#c9a24b]/50 hover:bg-[#c9a24b]/15 transition-colors">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 relative flex items-center justify-center overflow-hidden">
        {n > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous image" data-testid="photo-viewer-prev"
            className="absolute left-2 sm:left-4 z-10 h-11 w-11 flex items-center justify-center rounded-full bg-black/30 text-[#e9d9ac] border border-[#c9a24b]/40 hover:bg-[#c9a24b]/20 transition-colors">
            <ChevronLeft size={22} />
          </button>
        )}
        <div
          className="w-full h-full flex items-center justify-center touch-none px-2"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <img
            src={images[index]}
            alt={alt}
            draggable={false}
            onClick={(e) => { e.stopPropagation(); onClickImg(e); }}
            onMouseMove={onMoveImg}
            data-testid="photo-viewer-image"
            className="max-w-[94vw] max-h-full object-contain"
            style={{
              transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
              transformOrigin: COARSE ? "center" : origin,
              transition: gesturing ? "none" : "transform 0.18s ease-out",
              cursor: COARSE ? "default" : scale > 1 ? "zoom-out" : "zoom-in",
            }}
          />
        </div>
        {n > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next image" data-testid="photo-viewer-next"
            className="absolute right-2 sm:right-4 z-10 h-11 w-11 flex items-center justify-center rounded-full bg-black/30 text-[#e9d9ac] border border-[#c9a24b]/40 hover:bg-[#c9a24b]/20 transition-colors">
            <ChevronRight size={22} />
          </button>
        )}
      </div>

      {n > 1 && (
        <div className="flex items-center justify-center gap-2 px-4 py-4 overflow-x-auto">
          {images.map((img, i) => (
            <button key={img} onClick={(e) => { e.stopPropagation(); setScale(1); setTx(0); setTy(0); onIndexChange(i); }} data-testid={`photo-viewer-thumb-${i}`}
              aria-current={i === index}
              className={`w-14 h-14 shrink-0 overflow-hidden border transition-colors ${i === index ? "border-[#c9a24b]" : "border-white/20 hover:border-[#c9a24b]/60"}`}>
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};
